<!--
  Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
  This information is confidential and proprietary to GM Global Technology
  Operations LLC and may not be used, modified, copied or distributed.
  -->

<br/>
<h1 class="page-header">Aggregator IO Matrix</h1>

@if (permission()) {
  <div class="aggregator-io-matrix-page">
    @if (saving || exporting) {
      <p-progressBar mode="indeterminate" [style]="{ height: '6px' }"></p-progressBar>
    }

    <div class="context-row">
      <label>Object Code</label>
      <p-select [options]="objectCodes" [(ngModel)]="objectCode"
                [showClear]="!!objectCode"
                [disabled]="scopeSelectorsDisabled"
                placeholder="Object Code" (onChange)="onObjectCodeChanged()"></p-select>
      <label>Architecture</label>
      <p-select [options]="architectures" [(ngModel)]="architecture"
                [showClear]="!!architecture"
                [disabled]="scopeSelectorsDisabled || !objectCode"
                placeholder="Architecture" (onChange)="onArchitectureChanged()"></p-select>
      <label>Power Type</label>
      <p-select [options]="powerTypes" [(ngModel)]="powerType"
                [showClear]="!!powerType"
                [disabled]="scopeSelectorsDisabled || !objectCode || !architecture"
                placeholder="ICE / EV" (onChange)="onPowerTypeChanged()"></p-select>
      @if (keywordVisible()) {
        <label>Keyword</label>
        @if (keywordReadonly()) {
          <input class="keyword-readonly" [value]="keyword" disabled/>
        } @else {
          <p-select [options]="keywordOptions()" [(ngModel)]="keyword"
                    [showClear]="keywordShowClear(keyword, keywordOptions())"
                    [disabled]="scopeSelectorsDisabled"
                    placeholder="Keyword" (onChange)="onKeywordChanged()"></p-select>
        }
      }
    </div>
    @if (refMapWarning) {
      <p-message severity="warn" [closable]="true" data-testid="aggregator-ref-map-warning"
                 [text]="refMapWarningText"></p-message>
    }

    <div class="button-row" data-testid="table-actions">
      <p-button label="Auto Match" icon="pi pi-bolt" severity="info"
                class="mr-2"
                pTooltip="Suggest a Port/Signal Name for the empty rows using the Auto Match rules (see View Rules). Nothing is saved until you press Save."
                [disabled]="!canAutoMatch"
                [loading]="autoMatching"
                (click)="autoMatch()"
                (keydown)="onAutoMatchKeydown($event)"></p-button>
      <p-button label="View Rules" icon="pi pi-list" severity="secondary"
                class="mr-2"
                pTooltip="Show the rules Auto Match uses, including the configured circuit-to-port entries."
                data-testid="auto-match-view-rules"
                (click)="openAutoMatchRules()"
                (keydown)="onViewRulesKeydown($event)"></p-button>
      @if (scopeComplete) {
        <p-button label="Delete Matrix" icon="pi pi-trash" severity="danger"
                  class="mr-2"
                  [disabled]="!canDeleteMatrix"
                  (click)="confirmDeleteMatrix()"></p-button>
      }
      <p-button label="Download All History" icon="pi pi-history"
                class="mr-2"
                [disabled]="!canExportHistoryAll"
                (click)="exportHistoryAll()"></p-button>
      <p-button label="Download ICD Compare" icon="pi pi-file-excel"
                [disabled]="!canExportIcdCompare"
                (click)="exportIcdCompare()"
                (keydown)="onIcdCompareKeydown($event)"></p-button>
    </div>
    @if (scopeComplete) {
      <div class="button-row" data-testid="edit-actions">
        <p-button label="Reset Changes" icon="pi pi-replay" severity="warn"
                  class="mr-2"
                  [disabled]="!dataChangedSignal() || saving || autoMatching"
                  (click)="resetChanges()"></p-button>
        <p-button label="Save" icon="pi pi-save"
                  [disabled]="!dataChangedSignal() || saving || autoMatching || rowsInvalid"
                  [loading]="saving"
                  (click)="save()"></p-button>
      </div>
      <div class="editing-matrix-label">
        <strong>Editing Aggregator IO Matrix:</strong> {{ contextLabel }}
        <span class="cadence-label" data-testid="matrix-cadence">
          &nbsp;|&nbsp;<strong>Cadence:</strong>
        </span>
        <p-select [options]="cadenceOptionsSignal()"
                  [ngModel]="selectedCadenceVerSignal()"
                  (ngModelChange)="selectedCadenceVerSignal.set($event); onCadenceChanged()"
                  optionLabel="label" optionValue="value"
                  placeholder="Cadence" appendTo="body"
                  [disabled]="!canChangeCadence"
                  pTooltip="Save or reset the current changes before switching cadence."
                  [tooltipDisabled]="canChangeCadence"
                  data-testid="cadence-select"></p-select>
      </div>
      @if (deviceIoDriftDetected() && !anyMergePreviewActive()) {
        <p-message severity="warn" data-testid="device-io-drift-banner"
                   [text]="'Live Device IO differs from this saved matrix at cadence '
                           + selectedCadenceVerSignal() + '.'"></p-message>
        <div class="button-row">
          <p-button label="Sync from Device IO" icon="pi pi-sync" severity="warn"
                    [disabled]="!canStartDeviceIoSync" [loading]="syncingDeviceIo"
                    (click)="startDeviceIoSync()" data-testid="device-io-sync-start"></p-button>
        </div>
      }
      @if (cadenceUpgradeOffered() && !anyMergePreviewActive()) {
        <p-message severity="info" data-testid="cadence-upgrade-available"
                   [text]="'The Device IO source is now at cadence ' + sourceNewestCadenceVerSignal()
                           + '; this matrix is saved at cadence ' + latestSavedCadenceVerSignal() + '.'"></p-message>
        <div class="button-row">
          <p-button [label]="cadenceUpgradeLabel" icon="pi pi-arrow-up" severity="warn"
                    [disabled]="!canStartCadenceUpgrade" [loading]="upgrading"
                    (click)="startCadenceUpgrade()" data-testid="cadence-upgrade-start"></p-button>
        </div>
      }
      @if (deviceIoSyncPreviewActiveSignal()) {
        <p-message severity="warn" data-testid="device-io-sync-summary"
                   [text]="deviceIoSyncSummarySignal()"></p-message>
        <div class="button-row">
          <p-button label="Cancel Sync" icon="pi pi-times" severity="secondary"
                    (click)="cancelDeviceIoSync()" data-testid="device-io-sync-cancel"></p-button>
        </div>
      }
      @if (mergePreviewActiveSignal()) {
        <p-message severity="warn" data-testid="cadence-merge-summary"
                   [text]="mergeSummarySignal()"></p-message>
        <div class="button-row">
          <p-button label="Cancel Upgrade" icon="pi pi-times" severity="secondary"
                    (click)="cancelCadenceUpgrade()" data-testid="cadence-upgrade-cancel"></p-button>
        </div>
      }
    }

    <div class="component-catalog-grid-wrapper">
      <ag-grid-angular class="ag-theme-balham componentCatalog" style="width:100%; height:65vh"
                       [columnDefs]="cols" [rowData]="rowDataSignal()" [defaultColDef]="defaultColDef"
                       [rowClassRules]="rowClassRules"
                       [pagination]="true"
                       [paginationPageSize]="paginationPageSize"
                       [paginationPageSizeSelector]="paginationPageSizeSelector"
                       (cellEditingStopped)="cellEditingStopped($event, rowData)"
                       (cellValueChanged)="onCellValueChanged($event)"
                       (gridReady)="onGridReady($event)">
      </ag-grid-angular>
      <p-confirmDialog key="deleteMatrixConfirmation" esciConfirmEnterAccept defaultFocus="accept"></p-confirmDialog>
    </div>

    <p-dialog header="Auto Match Rules"
              [visible]="rulesDialogVisible()"
              (visibleChange)="rulesDialogVisible.set($event)"
              [modal]="true"
              [closable]="true"
              [draggable]="false"
              styleClass="auto-match-rules-dialog"
              [breakpoints]="{'960px': '96vw'}">
      <div class="auto-match-rules" data-testid="auto-match-rules">
        @if (loadingRules()) {
          <p-progressBar mode="indeterminate" styleClass="auto-match-rules-progress"></p-progressBar>
        }
        <p class="auto-match-rules-hint">
          Rules apply in this order to rows with no Port/Signal Name; a port is only suggested if it is in
          this matrix's Signal Table and not already used. Read-only: IT Admins edit the rules on
          Admin &gt; Configurations (Aggregator IO Matrix Auto Match Rules).
        </p>
        @if (autoMatchRules()?.invalidRules) {
          <p-message severity="warn" data-testid="auto-match-rules-invalid"
                     text="The Aggregator IO Matrix Auto Match Rules configuration is empty or invalid, so Auto Match applies no rules. Ask an IT Admin to set it on Configurations."></p-message>
        }
        <h4>Rules</h4>
        <table class="auto-match-rules-table">
          <thead><tr><th>Rule</th><th>Kind</th><th>Description</th></tr></thead>
          <tbody>
            @for (rule of autoMatchRules()?.rules || []; track $index) {
              <tr><td>{{ rule.ruleId }}</td><td>{{ rule.kind }}</td><td>{{ rule.description }}</td></tr>
            }
          </tbody>
        </table>
        <h4>Configured circuit-to-port entries (CIRCUIT_PORT)</h4>
        <table class="auto-match-rules-table">
          <thead><tr><th>Object Code</th><th>Circuit</th><th>Device IO Name</th><th>Port/Signal Name</th></tr></thead>
          <tbody>
            @for (entry of circuitPortRuleRows(); track $index) {
              <tr>
                <td>{{ entry.objectCode }}</td><td>{{ entry.circuit }}</td>
                <td>{{ entry.deviceIoName }}</td><td>{{ entry.signal }}</td>
              </tr>
            } @empty {
              @if (autoMatchRules()) {
                <tr><td colspan="4">No entries configured.</td></tr>
              }
            }
          </tbody>
        </table>
      </div>
    </p-dialog>
  </div>
}
